import { Chip, Divider, IconButton, Tooltip, Zoom } from "@mui/material";
import { DateTime } from "luxon";
import { Fragment, ReactElement, createElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbActivityHeartbeat, TbArrowDown, TbHistory } from "react-icons/tb";
import { z } from "zod";

import { softColors } from "@/constants/colors";
import { HotelClinicStatusItem, hotelClinicStatusItems } from "@/constants/hotelClinicStatuses";
import { HotelClinicStatus } from "@/models/hotel-clinic.model";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import { HotelClinicStatusMilestonesSchema } from "@/schemas/hotelClinicSchema";
import { useClinicArrivalTasksGridActions } from "@/stores/use-clinic-arrival-tasks-grid";

export type HotelClinicStatusMilestones = z.infer<typeof HotelClinicStatusMilestonesSchema>;

type MilestoneItem = NonNullable<HotelClinicStatusMilestones[HotelClinicStatus]>;

function useLastMilestoneIndex(statusMilestones: HotelClinicStatusMilestones): number {
    return useMemo(() => {
        for (let i = hotelClinicStatusItems.length - 1; i >= 0; i--) {
            if (statusMilestones[hotelClinicStatusItems[i].status]?.actionTime) return i;
        }
        return -1;
    }, [statusMilestones]);
}

export function HotelClinicTaskMilestonesTimeline({
    statusMilestones,
}: {
    statusMilestones: HotelClinicStatusMilestones;
}) {
    const lastWithTime = useLastMilestoneIndex(statusMilestones);
    const lastCompletedStatusIndex = lastWithTime + 1;

    const ordered = useMemo(
        () => hotelClinicStatusItems.map((item) => [item, statusMilestones[item.status]] as const),
        [statusMilestones]
    );

    return (
        <div className="px-3 pt-2">
            <ol className="relative flex flex-col gap-5 py-2 mx-2">
                <div className="absolute -left-[1px] w-[2px] bg-gray-200 h-[calc(100%+10px)] -top-[5px]" />
                <div
                    className="absolute -left-[1px] w-[2px] -top-[5px]"
                    style={{
                        height: `calc(${(lastCompletedStatusIndex + 1) * 20}% + 10px)`,
                        backgroundColor: softColors.indigo.hover,
                    }}
                />

                {ordered.map(([item, milestone], index) => (
                    <Fragment key={item.status}>
                        <li className="ms-6 flex gap-3 items-center">
                            <TaskMilestonesItem
                                item={item}
                                milestone={milestone}
                                index={index}
                                lastWithTimeIndex={lastWithTime}
                            />
                        </li>
                        {index !== ordered.length - 1 && (
                            <TaskMilestonesDivider time={milestone} nextTime={ordered[index + 1][1]} />
                        )}
                    </Fragment>
                ))}
            </ol>
        </div>
    );
}

function TaskMilestonesItem({
    item,
    milestone,
    index,
    lastWithTimeIndex,
}: {
    item: HotelClinicStatusItem;
    milestone: MilestoneItem | null;
    index: number;
    lastWithTimeIndex: number;
}) {
    const { t } = useTranslation();
    const isCompleted = lastWithTimeIndex >= 0 && index <= lastWithTimeIndex;

    return (
        <>
            <span
                style={{ backgroundColor: isCompleted ? softColors.indigo.bg : softColors.grey.bg }}
                className="absolute flex items-center justify-center w-8 h-8 shadow rounded-full -left-4 ring-1 ring-gray-300"
            >
                {createElement(item.component(), {
                    size: 22,
                    color: isCompleted ? softColors.indigo.text : softColors.grey.text,
                })}
            </span>
            <div className="flex flex-col w-72 min-h-10">
                <div className="font-semibold text-sm">{t(item.label)}</div>
                <span className="text-gray-500 font-normal text-xs">{t(item.tooltip.description)}</span>
            </div>
            <div className="shrink-0 grow-0 ml-auto w-12 text-right">
                {milestone?.actionTime ? (
                    <>
                        <div className="text-gray-500 font-normal text-xs">
                            {DateTime.fromISO(milestone.actionTime)
                                .setZone("Europe/Istanbul")
                                .setLocale("tr")
                                .toFormat("d LLL")}
                        </div>
                        <div className="text-gray-900 font-medium text-sm">
                            {DateTime.fromISO(milestone.actionTime)
                                .setZone("Europe/Istanbul")
                                .setLocale("tr")
                                .toFormat("HH:mm")}
                        </div>
                    </>
                ) : index === 0 ? (
                    ""
                ) : (
                    <div className="w-full h-full grid place-content-center">-</div>
                )}
            </div>
        </>
    );
}

function TaskMilestonesDivider({ time, nextTime }: { time: MilestoneItem | null; nextTime: MilestoneItem | null }) {
    if (!time?.actionTime || !nextTime?.actionTime) return null;
    const fromTime = DateTime.fromISO(time.actionTime).setZone("Europe/Istanbul").set({ second: 0 });
    const toTime = DateTime.fromISO(nextTime.actionTime).setZone("Europe/Istanbul").set({ second: 0 });
    const timeDiff = useMemo(() => {
        const diff = toTime.diff(fromTime, ["hours", "minutes"]);
        if (!diff.isValid) return null;
        return diff.hours > 0
            ? `${Math.floor(diff.hours)}h ${Math.round(diff.minutes)}m`
            : `${Math.round(diff.minutes)}m`;
    }, [fromTime, toTime]);

    if (!timeDiff) return null;

    return (
        <Chip
            className="!w-fit shrink-0 grow-0 mx-auto bg-gray-300 font-medium"
            icon={createElement(TbArrowDown, { fontSize: 10, color: "gray" })}
            label={`Geçiş: ${timeDiff}`}
            size="small"
        />
    );
}

function HotelClinicTaskMilestonesModalContent({
    statusMilestones,
    taskId,
}: {
    statusMilestones: HotelClinicStatusMilestones;
    taskId: string;
}) {
    const { t } = useTranslation();
    const { updateTaskStatusHistoryTaskId } = useClinicArrivalTasksGridActions();

    return (
        <div
            className="text-sm py-2 flex flex-col gap-1 max-w-md"
            onDoubleClick={(e) => {
                e.stopPropagation();
            }}
        >
            <div className="flex items-center justify-between gap-2 px-1">
                <div className="flex items-center gap-1 font-medium text-base">
                    <TbActivityHeartbeat size={22} />
                    {t("hotelClinicStatuses.milestoneTrackingTitle")}
                </div>
                <Tooltip title={t("hotelClinicStatuses.viewHistoryTooltip")}>
                    <IconButton size="small" component="div" onClick={() => updateTaskStatusHistoryTaskId(taskId)}>
                        <TbHistory />
                    </IconButton>
                </Tooltip>
            </div>
            <Divider className="!bg-gray-200 !w-[80%] !mx-auto" />
            <HotelClinicTaskMilestonesTimeline statusMilestones={statusMilestones} />
        </div>
    );
}

export function HotelClinicTaskStatusMilestonesModal({
    children,
    statusMilestones,
    taskId,
}: {
    children: ReactElement;
    statusMilestones: HotelClinicStatusMilestones | undefined;
    taskId: string;
}) {
    if (!statusMilestones) return children;

    return (
        <NoMaxWidthTooltip
            title={<HotelClinicTaskMilestonesModalContent statusMilestones={statusMilestones} taskId={taskId} />}
            slots={{
                transition: Zoom,
            }}
            arrow
        >
            {children}
        </NoMaxWidthTooltip>
    );
}
